<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('editQuestion.title') }}
            </h6>
          </div>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="t('editQuestion.loading')" />

      <div v-else class="card-body px-0 pb-2">
        <QuestionForm
          ref="questionForm"
          v-if="questionData"
          :initial-data="questionData"
          :is-loading="isUpdating"
          :is-edit-mode="true"
          :submit-text="t('editQuestion.submit')"
          @submit="updateQuestion"
          @cancel="navigateBack"
        />
        <div class="d-flex gap-3 mt-3 mx-2">
          <button
            type="button"
            class="btn btn-primary btn-sm flex-grow-1"
            @click="questionForm?.submitForm()"
            :disabled="isUpdating || !hasChanges"
          >
            <span v-if="isUpdating">
              <i class="fas fa-spinner fa-spin ms-2"></i>
              {{ t('editQuestion.saving') }}
            </span>
            <span v-else>
              <i class="fas fa-save ms-2"></i>
              {{ t('editQuestion.submit') }}
            </span>
          </button>

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm flex-grow-1"
            @click="navigateBack"
            :disabled="isUpdating"
          >
            <i class="fas fa-times ms-2"></i>
            {{ t('common.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import QuestionForm from "@/components/features/questions/QuestionForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const toast = useToast();
const store = useStore();

const isLoading = ref(false);
const isUpdating = ref(false);
const questionForm = ref(null);
const questionData = ref(null);

const hasChanges = computed(() => {
  if (!questionData.value || !questionForm.value?.formData) return false;
  
  const current = JSON.parse(JSON.stringify(questionForm.value.formData));
  const initial = JSON.parse(JSON.stringify(questionData.value));
  
  const normalizeAnswers = (answers) => {
    return (answers || []).map(a => ({
      answer: a.answer || '',
      is_correct: Boolean(a.is_correct),
      question_part: a.question_part || ''
    }));
  };
  
  current.answers = normalizeAnswers(current.answers);
  initial.answers = normalizeAnswers(initial.answers);
  
  const formFields = [
    'description', 'class_id', 'subject_id', 'chapter_subject_id',
    'question_type_id', 'question_level_id', 'teacher_id', 'image'
  ];
  
  const formChanged = formFields.some(field => 
    JSON.stringify(current[field]) !== JSON.stringify(initial[field])
  );
  
  const answersChanged = JSON.stringify(current.answers) !== JSON.stringify(initial.answers);
  
  return formChanged || answersChanged;
});

const fetchQuestion = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch("questions/fetchQuestion", route.params.id);
    const question = response.data || response;
    
    questionData.value = {
      ...question,
      class_id: question.class?.id || question.class_id || null,
      subject_id: question.subject?.id || question.subject_id || null,
      chapter_subject_id: question.chapter_subject?.id || question.chapter_subject_id || null,
      question_type_id: question.question_type?.id || question.question_type_id || null,
      question_level_id: question.question_level?.id || question.question_level_id || null,
      teacher_id: question.teacher?.id || question.teacher_id || null,
      image: question.image || null,
      image_url: question.image 
        ? question.image.startsWith('http') 
          ? question.image 
          : `/storage/${question.image}`
        : null,
      answers: question.answers ? question.answers.map(answer => ({
        ...answer,
        is_correct: Boolean(answer.is_correct),
        answer: answer.answer || "",
        question_part: answer.question_part || ""
      })) : []
    };
    
  } catch (error) {
    toast.error(t('editQuestion.fetchError'));
    router.push({ name: "questions.index" });
  } finally {
    isLoading.value = false;
  }
};

const updateQuestion = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t('editQuestion.noChanges'));
    return;
  }

  isUpdating.value = true;
  try {
    await store.dispatch("questions/updateQuestion", {
      id: route.params.id,
      questionData: formData,
    });
    toast.success(t('editQuestion.success'));
    router.push({ name: "questions.index" });
  } catch (error) {
    toast.error(error.response?.data?.message || t('editQuestion.updateError'));
    console.error("Error updating question:", error);
  } finally {
    isUpdating.value = false;
  }
};

const navigateBack = () => {
  router.push({ name: "questions.index" });
};

onMounted(fetchQuestion);
</script>
